import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { CartProvider } from "./hooks/useCart";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { SiteImagesProvider } from "./hooks/useSiteImages";
import Navigation from "./components/layout/Navigation";
import CartDrawer from "./components/layout/CartDrawer";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import ETicketsPage from "./pages/ETicketsPage";
import MedalsPage from "./pages/MedalsPage";
import EventsPage from "./pages/EventsPage";
import MerchPage from "./pages/MerchPage";
import ReviewsPage from "./pages/ReviewsPage";
import ContactPage from "./pages/ContactPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import { PrivacyPage, RefundPage } from "./pages/PolicyPages";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLogin from "./pages/admin/AdminLogin";
import AllDonePage from "./pages/AllDonePage";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin w-10 h-10 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppContent() {
  const hiddenLayoutPaths = ["/alldone"];

  return (
    <BrowserRouter>
      <div className="App">
        <ScrollToTop />
        <div className="noise-overlay" />
        <LayoutWrapper hiddenPaths={hiddenLayoutPaths} />
      </div>
    </BrowserRouter>
  );
}

function LayoutWrapper({ hiddenPaths }) {
  const { pathname } = useLocation();
  const hideLayout = hiddenPaths.some(p => pathname === p || pathname === p + "/");

  return (
    <>
      {!hideLayout && <Navigation />}
      {!hideLayout && <CartDrawer />}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/e-tickets" element={<ETicketsPage />} />
          <Route path="/medals" element={<MedalsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/merch" element={<MerchPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/alldone" element={<AllDonePage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      {!hideLayout && <Footer />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Toaster richColors position="top-center" />
      <AuthProvider>
        <SiteImagesProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </SiteImagesProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
